import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const filteredArticles = articles.filter((a) => a.category === slug);
  const [sortBy, setSortBy] = useState<'latest' | 'hot'>('latest');

  const sortedArticles = [...filteredArticles].sort((a, b) => {
    if (sortBy === 'hot') return b.views - a.views;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - 黑料入口 | 51吃瓜黑料入口一二三`;
    }
  }, [category]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">分类未找到</h1>
          <Link to="/" className="text-primary-500 text-sm mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <header>
        <Navbar />
      </header>

      <main className="pt-24 pb-16 bg-background-50">
        <div className="mx-auto px-4 md:px-6 max-w-[1440px]">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{category.name}</span>
          </nav>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heiliaorukou.com/' },
                  { '@type': 'ListItem', position: 2, name: category.name, item: `https://heiliaorukou.com/category/${slug}` },
                ],
              }),
            }}
          />

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className={`${category.icon} text-lg text-primary-500`}></i>
                </div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                  {category.name}
                </h1>
              </div>
              <p className="text-sm text-foreground-500">{category.description}</p>
            </div>

            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              <button
                onClick={() => setSortBy('latest')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  sortBy === 'latest' ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                最新
              </button>
              <button
                onClick={() => setSortBy('hot')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  sortBy === 'hot' ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                最热
              </button>
            </div>
          </div>

          {sortedArticles.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300 mb-4"></i>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {sortedArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="w-full h-[200px] overflow-hidden bg-background-100 relative">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={`黑料入口 ${article.title}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    {article.isHot && (
                      <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-primary-500 text-white text-xs font-medium whitespace-nowrap">
                        热门
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <h2 className="font-serif text-base font-bold text-foreground-950 group-hover:text-primary-500 transition-colors leading-snug line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-500 mt-2 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 mt-3">
                      <span className="text-xs text-foreground-500">{article.date}</span>
                      <span className="text-xs text-foreground-400">·</span>
                      <div className="flex items-center gap-1">
                        <i className="ri-eye-line text-xs text-foreground-400"></i>
                        <span className="text-xs text-foreground-500">
                          {article.views >= 10000 ? `${(article.views / 10000).toFixed(0)}万` : article.views}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {sortedArticles.length > 0 && (
            <div className="flex justify-center mt-10 gap-2">
              <button className="w-10 h-10 rounded-full bg-foreground-950 text-background-50 flex items-center justify-center text-sm font-medium cursor-pointer">1</button>
              <button className="w-10 h-10 rounded-full border border-background-300 flex items-center justify-center text-sm text-foreground-500 hover:bg-background-100 cursor-pointer">2</button>
              <button className="w-10 h-10 rounded-full border border-background-300 flex items-center justify-center text-sm text-foreground-500 hover:bg-background-100 cursor-pointer">
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}